import { type ReactNode } from 'react'
import { motion } from 'framer-motion'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#D4C3EA'
const GOLD = '#C8923A'
const PHOTO_SRC = 'https://static.lumi.new/66/66a59164029fe3389411bfef927c8644.webp'

const SOCIALS: Array<{ label: string; href: string; icon: ReactNode }> = [
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/@reacendaluz',
    icon: (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
        <path d="M23.5 6.2a3.05 3.05 0 0 0-2.15-2.16C19.5 3.6 12 3.6 12 3.6s-7.5 0-9.35.44A3.05 3.05 0 0 0 .5 6.2 31.9 31.9 0 0 0 0 12a31.9 31.9 0 0 0 .5 5.8 3.05 3.05 0 0 0 2.15 2.16C4.5 20.4 12 20.4 12 20.4s7.5 0 9.35-.44a3.05 3.05 0 0 0 2.15-2.16A31.9 31.9 0 0 0 24 12a31.9 31.9 0 0 0-.5-5.8ZM9.75 15.57V8.43L15.84 12l-6.09 3.57Z" />
      </svg>
    ),
  },
  {
    label: 'WhatsApp',
    href: 'https://wa.me/5581988671210',
    icon: (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
        <path d="M12.04 2C6.5 2 2 6.37 2 11.76c0 1.72.46 3.4 1.34 4.88L2 22l5.53-1.43a10.2 10.2 0 0 0 4.51 1.07h.01c5.54 0 10.04-4.37 10.04-9.76C22.09 6.37 17.58 2 12.04 2Zm5.83 13.8c-.25.7-1.45 1.29-2.01 1.37-.51.08-1.16.11-1.87-.12-.43-.14-.98-.32-1.69-.62-2.97-1.27-4.9-4.23-5.05-4.43-.15-.2-1.22-1.6-1.22-3.06 0-1.45.77-2.17 1.04-2.47.27-.3.59-.37.79-.37h.57c.18 0 .42-.07.66.5.25.59.85 2.05.92 2.2.07.15.12.33.02.53-.1.2-.15.33-.3.5-.15.18-.31.4-.44.53-.15.15-.3.31-.13.6.17.3.76 1.24 1.63 2.01 1.12.99 2.06 1.3 2.36 1.45.3.15.47.12.65-.07.17-.2.75-.86.95-1.16.2-.3.4-.25.67-.15.27.1 1.72.8 2.01.95.3.15.5.22.57.34.07.12.07.7-.18 1.4Z" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/reacendaluz',
    icon: (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
        <path d="M14.5 8.5V6.7c0-.7.5-1 1-1h1.9V3h-2.6C11.9 3 11 5.1 11 6.9v1.6H9v2.7h2V21h3.5v-9.8h2.4l.4-2.7h-2.8Z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/reacendaluz/',
    icon: (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
        <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
        <circle cx="12" cy="12" r="4.2" />
        <circle cx="17.2" cy="6.8" r="1" fill="currentColor" stroke="none" />
      </svg>
    ),
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@reacendaluz',
    icon: (
      <svg viewBox="0 0 24 24" className="w-[18px] h-[18px]" fill="currentColor" aria-hidden="true">
        <path d="M14.6 3c.3 2.4 1.6 4.1 4 4.5v2.5c-1.4.14-2.7-.22-3.9-.9v6.3c0 3.4-2.6 5.8-6.1 5.8A6 6 0 0 1 4.5 14.3c.4-3.2 3.3-5.5 6.6-5.2v2.7a3.3 3.3 0 0 0-3.5 3.3c.1 1.8 1.6 3.1 3.4 3.1 1.9 0 3.4-1.5 3.4-3.5V3h.2Z" />
      </svg>
    ),
  },
]

const PATHS: Array<{
  title: string
  subtitle?: string
  href: string
  tone: 'gold' | 'lilac'
}> = [
  {
    title: 'Quero descobrir o que me bloqueia (Teste Gratuito)',
    href: 'https://elementos.reacendaluz.com.br/',
    tone: 'gold',
  },
  {
    title: 'Quero um mapa das minhas dores e bloqueios',
    href: 'https://elementos.reacendaluz.com.br/mapa',
    tone: 'lilac',
  },
  {
    title: 'Quero um protocolo para liberar o que me prende e criar uma nova vida',
    href: 'https://jornada.reacendaluz.com.br/protocolo/',
    tone: 'lilac',
  },
  {
    title: 'Quero uma leitura do meu campo vibracional',
    href: 'https://leitura.reacendaluz.com.br/',
    tone: 'lilac',
  },
  {
    title: 'Quero um atendimento direto com Mônica Maria',
    href: 'https://jornada.reacendaluz.com.br/atendimentos',
    tone: 'lilac',
  },
]

export default function PageApresentacao() {
  const host = typeof window !== 'undefined'
    ? window.location.hostname.replace(/^www\./, '').toLowerCase()
    : ''
  const isApexHost = host === 'reacendaluz.com.br'

  usePageMeta({
    title: 'Mônica Maria — Reacenda Luz',
    description: 'Você chegou até aqui porque algo dentro de você sabe que é hora de mudar. Escolha por onde começar.',
    ogImage: PHOTO_SRC,
    ogUrl: isApexHost
      ? 'https://reacendaluz.com.br/'
      : 'https://jornada.reacendaluz.com.br/bio/',
  })

  return (
    <div className="min-h-screen w-full font-body bg-ambient" style={{ color: 'var(--ink, #ece4d1)' }}>
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.08 }}
            className="flex justify-center mb-8"
          >
            <div className="relative">
              <div
                className="absolute -inset-6 rounded-full pointer-events-none"
                style={{ background: `radial-gradient(closest-side, ${ACCENT}28, transparent 72%)` }}
              />
              <div
                className="relative w-36 h-36 sm:w-44 sm:h-44 rounded-full overflow-hidden"
                style={{
                  border: `2px solid ${ACCENT}66`,
                  boxShadow: `0 0 36px ${ACCENT}28`,
                }}
              >
                <img
                  src={PHOTO_SRC}
                  alt="Mônica Maria — Terapeuta Vibracional"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.22 }}
            className="font-display type-hero mb-2"
            style={{ color: 'var(--ink)' }}
          >
            Mônica Maria
          </motion.h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.32 }}
            className="font-display italic mb-6"
            style={{ color: ACCENT_SOFT, fontSize: 'clamp(1.2rem, 2.6vw, 1.45rem)' }}
          >
            Reacenda Luz
          </motion.p>

          <motion.nav
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.38 }}
            aria-label="Redes sociais"
            className="flex items-center justify-center gap-3 mb-8"
          >
            {SOCIALS.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:brightness-125 active:scale-95"
                style={{
                  color: ACCENT_SOFT,
                  border: `1px solid ${ACCENT}55`,
                  background: `${ACCENT}14`,
                }}
              >
                {social.icon}
              </a>
            ))}
          </motion.nav>

          <motion.div
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 72, opacity: 1 }}
            transition={{ duration: 1.1, delay: 0.44 }}
            className="h-px mx-auto mb-8"
            style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}aa, transparent)` }}
          />

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.85, delay: 0.45 }}
            className="space-y-5 mb-12 text-left sm:text-center"
          >
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.82)' }}>
              Você chegou até aqui porque algo dentro de você sabe que é hora de mudar.
            </p>
            <p className="font-body type-body leading-relaxed" style={{ color: 'rgba(232,224,213,0.78)' }}>
              Sou Mônica Maria, Terapeuta Vibracional. Meu trabalho é revelar o que te prende e elevar sua vibração para você criar a vida que você merece.
            </p>
          </motion.div>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
        >
          <h2
            className="font-display type-subtitle text-center mb-7"
            style={{ color: ACCENT_SOFT }}
          >
            Por onde você quer começar?
          </h2>

          <div className="flex flex-col gap-3.5">
            {PATHS.map((path, i) => {
              const isGold = path.tone === 'gold'
              return (
                <motion.a
                  key={path.href}
                  href={path.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55, delay: 0.62 + i * 0.08 }}
                  className="block w-full text-center px-5 py-5 min-h-[64px] rounded-lg font-body type-button leading-snug transition-all duration-300 active:scale-[0.98] hover:brightness-110"
                  style={
                    isGold
                      ? {
                          background: GOLD,
                          color: '#ffffff',
                          boxShadow: '0 8px 24px rgba(200,146,58,0.28)',
                          border: '1px solid rgba(255,255,255,0.08)',
                        }
                      : {
                          background: `linear-gradient(135deg, ${ACCENT}14 0%, rgba(255,255,255,0.03) 100%)`,
                          color: 'var(--ink, #ece4d1)',
                          border: `1px solid ${ACCENT}44`,
                        }
                  }
                >
                  {path.title}
                  {path.subtitle ? (
                    <>
                      <br />
                      {path.subtitle}
                    </>
                  ) : null}
                </motion.a>
              )
            })}
          </div>
        </motion.section>
      </main>

      <footer
        className="w-full px-4 py-8 text-center border-t mt-8"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="font-display italic text-[15px] mb-2" style={{ color: `${ACCENT_SOFT}99` }}>
          Com amor e presença, Mônica
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz
        </p>
      </footer>
    </div>
  )
}
